import { createGlobalStyle } from 'styled-components';
import fonts from './fonts';
import variables from './variables';
import TransitionStyles from './TransitionStyles';
import PrismStyles from './PrismStyles';

const GlobalStyle = createGlobalStyle`
  ${fonts};
  ${variables};

  html {
    box-sizing: border-box;
    width: 100%;
    scroll-behavior: smooth;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  ::selection {
    background-color: var(--accent-main);
    color: var(--text-main);
  }

  :focus {
    outline: 2px dashed var(--accent-main);
    outline-offset: 3px;
  }

  :focus:not(:focus-visible) {
    outline: none;
    outline-offset: 0px;
  }

  :focus-visible {
    outline: 2px dashed var(--accent-main);
    outline-offset: 3px;
  }

  html {
    scrollbar-width: thin;
    scrollbar-color: var(--text-dark) var(--bg-color);
  }
  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-track {
    background: var(--bg-color);
  }
  ::-webkit-scrollbar-thumb {
    background-color: var(--text-dark);
    border: 3px solid var(--bg-color);
    border-radius: 10px;
  }

  body {
    margin: 0;
    width: 100%;
    min-height: 100%;
    overflow-x: hidden;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    background-color: var(--bg-color);
    color: var(--text-muted);
    font-family: var(--font-body);
    font-size: var(--fz-xl);
    line-height: 1.5;

    @media (max-width: 480px) {
      font-size: var(--fz-md);
    }

    &.hidden {
      overflow: hidden;
    }

    &.blur {
      overflow: hidden;
      header {
        background-color: transparent;
      }
      #content > * {
        filter: blur(5px) brightness(0.7);
        transition: var(--transition);
        pointer-events: none;
        user-select: none;
      }
    }
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  main {
    margin: 0 auto;
    width: 100%;
    max-width: 1440px;
    min-height: 100vh;
    padding: calc(var(--nav-height) + 40px) 100px 100px;

    @media (max-width: 1080px) {
      padding: calc(var(--nav-height) + 40px) 60px 80px;
    }
    @media (max-width: 768px) {
      padding: calc(var(--nav-height) + 20px) 40px 60px;
    }
    @media (max-width: 480px) {
      padding: calc(var(--nav-height) + 20px) 20px 40px;
    }
  }

  section {
    margin: 0 auto;
    padding: 60px 0;
    max-width: 1200px;
    scroll-margin-top: calc(var(--nav-height) + 25px);

    @media (max-width: 768px) {
      padding: 40px 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 20px 0;
    font-family: var(--font-heading);
    font-weight: 700;
    color: var(--text-main);
    line-height: 1.1;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .big-heading {
    margin: 0;
    font-size: clamp(50px, 8vw, 90px);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: -0.02em;
  }

  .medium-heading {
    margin: 0;
    font-size: clamp(30px, 6vw, 60px);
    font-weight: 700;
  }

  img, svg, .gatsby-image-wrapper {
    width: 100%;
    max-width: 100%;
    vertical-align: middle;
  }

  img[alt=""], img:not([alt]) {
    filter: blur(5px);
  }

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
    vertical-align: middle;
    &.feather {
      fill: none;
    }
  }

  a {
    display: inline-block;
    text-decoration: none;
    text-decoration-skip-ink: auto;
    color: inherit;
    position: relative;
    transition: var(--transition);

    &:hover,
    &:focus {
      color: var(--accent-main);
    }
    &.inline-link {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  button {
    cursor: pointer;
    border: 0;
    border-radius: 0;
  }

  p {
    margin: 0 0 20px 0;
    &:last-child, &:last-of-type {
      margin: 0;
    }
    & > a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
    & > code {
      background-color: var(--surface-color);
      color: var(--text-main);
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      border-radius: 6px;
      padding: 0.2em 0.4em;
    }
  }

  ul.fancy-list {
    padding: 0;
    margin: 0;
    list-style: none;
    font-size: var(--fz-lg);
    li {
      position: relative;
      padding-left: 30px;
      margin-bottom: 10px;
      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--accent-main);
      }
    }
  }

  code {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }

  .skip-to-content {
    ${({ theme }) => theme.mixins.button};
    position: absolute;
    top: auto;
    left: -999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: -99;
    &:hover, &:focus {
      background-color: var(--accent-main);
      color: var(--text-main);
      top: 0;
      left: 0;
      width: auto;
      height: auto;
      overflow: auto;
      z-index: 99;
      box-shadow: none;
      transform: none;
    }
  }

  ${TransitionStyles};
  ${PrismStyles};
`;

export default GlobalStyle;
